import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InterviewRecordPage = () => {
  const { projectId, interviewId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [interview, setInterview] = useState(null);
  
  const [intervieweeName, setIntervieweeName] = useState('');
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    const fetchInterview = async () => {
      try {
        const res = await axios.get(`${API}/interviews/${interviewId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInterview(res.data);
        // Initialize answers
        const initialAnswers = {};
        res.data.questions.forEach(q => {
          initialAnswers[q.id] = '';
        });
        setAnswers(initialAnswers);
      } catch (err) {
        toast.error('Failed to load interview');
        navigate(`/project/${projectId}/data-collection`);
      } finally {
        setLoading(false);
      }
    };
    fetchInterview();
  }, [interviewId, projectId, token, navigate]);

  const handleSave = async () => {
    // Check if any answers are provided
    const hasAnswers = Object.values(answers).some(a => a.trim());
    if (!hasAnswers) {
      toast.error('Please record at least one response');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/interviews/${interviewId}/responses`, {
        interviewee_id: uuidv4(),
        interviewee_name: intervieweeName.trim() || `Participant ${(interview.interviewees?.length || 0) + 1}`,
        answers,
        recorded_at: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Interview response saved');
      navigate(`/project/${projectId}/interview/${interviewId}/responses`);
    } catch (err) {
      toast.error('Failed to save interview response');
    } finally {
      setSaving(false);
    }
  };

  const goToQuestion = (index) => {
    if (index >= 0 && index < interview.questions.length) {
      setCurrentQuestionIndex(index);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const currentQuestion = interview?.questions?.[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/project/${projectId}/interview/${interviewId}/responses`)} 
                className="text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">Record Interview</h1>
                <p className="text-sm text-slate-500">{interview?.title}</p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Response'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Interviewee Info */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <Label>Interviewee Name (optional)</Label>
              <Input
                value={intervieweeName}
                onChange={(e) => setIntervieweeName(e.target.value)}
                placeholder="e.g., Participant 1 or John D."
                className="mt-1"
                data-testid="interviewee-name-input"
              />
            </div>
          </div>
          <p className="text-sm text-slate-500">
            Recording interview #{(interview?.interviewees?.length || 0) + 1}
            {interview?.target_interviewees && ` of ${interview.target_interviewees} target`}
          </p>
        </div>

        {/* Question Navigation */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-slate-500">
              Question {currentQuestionIndex + 1} of {interview?.questions?.length}
            </span>
            <div className="flex gap-2">
              {interview?.questions?.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToQuestion(idx)}
                  className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                    idx === currentQuestionIndex
                      ? 'bg-emerald-600 text-white'
                      : answers[interview.questions[idx].id]?.trim()
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-slate-100 rounded-full h-2">
            <div 
              className="bg-emerald-600 h-2 rounded-full transition-all"
              style={{ 
                width: `${(Object.values(answers).filter(a => a.trim()).length / interview.questions.length) * 100}%` 
              }}
            />
          </div>
        </div>

        {/* Current Question */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <span className="text-sm text-emerald-600 font-medium">Question {currentQuestion?.order}</span>
              <h2 className="text-xl font-semibold text-slate-900 mt-1">{currentQuestion?.question}</h2>
            </div>
          </div>
          
          <div>
            <Label>Interviewee Response</Label>
            <textarea
              value={answers[currentQuestion?.id] || ''}
              onChange={(e) => setAnswers({ ...answers, [currentQuestion?.id]: e.target.value })}
              placeholder="Type the interviewee's response here... You can transcribe their spoken words or summarize their answer."
              className="w-full mt-2 px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent min-h-[200px] text-lg"
              data-testid="response-textarea"
            />
            <p className="text-sm text-slate-500 mt-2">
              Tip: Record the response verbatim for best analysis results
            </p>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => goToQuestion(currentQuestionIndex - 1)}
            disabled={currentQuestionIndex === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <div className="text-sm text-slate-500">
            {Object.values(answers).filter(a => a.trim()).length} of {interview?.questions?.length} answered
          </div>
          
          {currentQuestionIndex < interview?.questions?.length - 1 ? (
            <Button
              onClick={() => goToQuestion(currentQuestionIndex + 1)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Finish & Save
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default InterviewRecordPage;
